import React, { useMemo } from 'react';
import {
  FIELDS,
  FIELDS1,
  FIELD_NAMES,
  getMetricInfoListUI,
} from '@gp/utils/metricTips';
import ProjectItem from '@sg/components/ProjectItem';
import InfoBubble from '@gp/components/InfoBubble';
import cls from 'classnames';
import type { IRecord } from '@gp/types/global';
import CommonValueTd from '@gp/components/GeneralTable/components/CommonValueTd';
import { cloneDeep, isEmpty } from 'lodash-es';
import { useFieldCollapse } from '../../../hooks/useFieldCollapse';
import { LeftTriangle } from '../../../components/LeftTriangle';
import CommonBubble from '@home/components/CommonBubble';
import {
  centerModalKey,
  footerModalKey,
  topModalKey,
} from '../../LiveVisualization/component/LiveCardWithRank';
import { detectPlatforms } from '../../../utils';
import moment from 'moment';
import PlatformIcon from '@gp/components/PlatformIcon';

export const getMetricInfoListUICommon = (data: IRecord<any>) =>
  <div className='dbs-metric-info-list'>
    {getMetricInfoListUI(FIELDS1, {
      ...FIELD_NAMES,
      definition: `${FIELD_NAMES.definition}:`,
      source: `${FIELD_NAMES.source}:`,
      updateCadence: `${FIELD_NAMES.updateCadence}:`,
    }, data)}
  </div>

export const METRIC_NAME_LIST = {
  ...topModalKey,
  ...centerModalKey,
  ...footerModalKey,
};

// 将给定的指标对象转换为metrics数组
export function convertMetricsToArray(metricsObj) {
  return Object.keys(metricsObj).map(key => ({
    metric: key,
    value: null,
    yoy: null,
    mom: null
  }));
}

/**
 * 扁平化 metrics 数据，将 metrics 中除 yoy/mom 外的属性提升到父级
 * @param data 待处理的数据数组
 * @returns 处理后的扁平化数据
 */
export const flattenMetrics = (data?: any) => {
  if (isEmpty(data)) return [];

  return data?.map((item: any) => {
    const { metrics = [], ...rest } = item;

    return metrics.reduce(
      (acc, { metric: metricKey, yoy, mom, ...metricProps }) => {
        // 处理普通属性
        Object.entries(metricProps).forEach(([key, value]) => {
          acc[`${metricKey}_${key}`] = value;
          acc.mergeDate = `${moment(item.start_date).format(
            'YYYY-MM',
          )} ~ ${moment(item.end_date).format('YYYY-MM')}`;
        });

        // 处理 yoy 数据
        yoy &&
          Object.entries(yoy).forEach(([key, val]) => {
            acc[`${metricKey}_yoy_${key}`] =
              typeof val === 'number' ? val / 100 : val;
          });

        // 处理 mom 数据
        mom &&
          Object.entries(mom).forEach(([key, val]) => {
            acc[`${metricKey}_mom_${key}`] =
              typeof val === 'number' ? val / 100 : val;
          });

        return acc;
      },
      { ...rest },
    );
  });
};

/**
 * 扁平化 dimension_breakdown 聚合数据（isAggregate 为 true）
 * 参考 flattenMetrics：Project/Studio 取父级，Date 由父级 start_date ~ end_date 组合，
 * Platform/Region/Country 取 breakdown，指标取 breakdown.metrics
 * @param data 待处理的数据数组（data.list）
 * @returns 处理后的扁平化数据
 */
export const flattenMetricsBreakdown = (data?: any) => {
  if (isEmpty(data)) return [];

  return data?.flatMap((item: any) => {
    const { dimension_breakdown: dimensionBreakdown = [], ...rest } = item;
    const mergeDate = `${moment(item.start_date).format('YYYY-MM')} ~ ${moment(item.end_date).format('YYYY-MM')}`;

    return (dimensionBreakdown || []).map((breakdown: any) => {
      const { metrics: breakdownMetrics, platform, region_name_en, country_name_en } = breakdown || {};

      return (breakdownMetrics || []).reduce(
        (acc: any, { metric: metricKey, yoy, mom, ...metricProps }: any) => {
          // 处理普通属性（value 等）
          Object.entries(metricProps).forEach(([key, value]) => {
            acc[`${metricKey}_${key}`] = value;
          });

          // 处理 yoy 数据
          yoy &&
            Object.entries(yoy).forEach(([key, val]) => {
              acc[`${metricKey}_yoy_${key}`] = typeof val === 'number' ? val / 100 : val;
            });

          // 处理 mom 数据
          mom &&
            Object.entries(mom).forEach(([key, val]) => {
              acc[`${metricKey}_mom_${key}`] = typeof val === 'number' ? val / 100 : val;
            });

          return acc;
        },
        {
          ...rest,
          mergeDate,
          platform: platform || '-',
          region: region_name_en || '-',
          country: country_name_en || '-',
        },
      );
    });
  });
};

const getActualTdOpt = (
  valueKey: string,
  yoyKey: string,
  momKey: string,
  isSelectYoy: boolean,
  isSelectMom: boolean,
) => {
  let tendencyOpt: any = null;
  if (isSelectYoy && isSelectMom) {
    tendencyOpt = {
      type: 'tendency',
      value: yoyKey,
      style: { marginLeft: 10 },
      isTendencyLabel: true,
      label: 'YoY',
      isRenderDoubleTendency: true,
      doubleTendencyValue: momKey,
      doubleLabel: 'MoM',
    };
  } else if (isSelectYoy) {
    tendencyOpt = {
      type: 'tendency',
      value: yoyKey,
      style: { marginLeft: 10 },
      isTendencyLabel: true,
      label: 'YoY',
    };
  } else if (isSelectMom) {
    tendencyOpt = {
      type: 'tendency',
      value: momKey,
      style: { marginLeft: 10 },
      isTendencyLabel: true,
      label: 'MoM',
    };
  }

  return {
    number: {
      options: [
        {
          type: 'value',
          value: valueKey,
          isTitle: true,
          isFormat: true,
          style: { maxWidth: 70 },
        },
        tendencyOpt,
      ],
    },
  };
};

/**
 * 获取表格配置项
 */
export const useLiveGetColumns = ({ yoy, mom, metricTipsData }: any) => {
  const METRIC_NAME_LIST = {
    ...topModalKey,
    ...centerModalKey,
    ...footerModalKey,
  };
  const {
    collapsedFields,
    toggleFieldDependencies,
    formFields,
    isFieldCollapsed,
  } = useFieldCollapse({
    formFields: ['gross_revenue', 'net_revenue', 'unit_sales'],
    collapseMap: {
      gross_revenue: [
        {
          field: 'gross_base_sales',
          isCollapsed: false,
        },
        {
          field: 'gross_gaas_sales',
          isCollapsed: false,
        },
      ],
      net_revenue: [
        {
          field: 'net_base_sales',
          isCollapsed: false,
        },
        {
          field: 'net_gaas_sales',
          isCollapsed: false,
        },
      ],
      unit_sales: [
        {
          field: 'base_sales',
          isCollapsed: false,
        },
        {
          field: 'dlc_sales',
          isCollapsed: false,
        },
      ],
    },
  });
  const defaultColumns = useMemo(
    () => ({
      isTips: true,
      isHasDoubleTitle: true,
      width: 220,
      align: 'right',
    }),
    [],
  );
  const columnsOptions = useMemo(() => {
    const res = [
      {
        keyName: 'project',
        title: 'Project',
        isHasDoubleTitle: true,
        width: 240,
        align: 'left',
        fixed: 'left',
        tdOpt: {
          project: {
            renderSolt: (cvm: any) => (
              <>
                <p style={{ color: '#838697', marginTop: '-5px' }}>{cvm?.developer || '-'}</p>
                {/* <PlatformIcon platform={detectPlatforms(cvm?.os_type)} /> */}
              </>
            ),
            platformTranformSolt: true,
            domain: 'game',
            src: 'cover',
            title: 'game_title',
            platform: 'os_type',
            platformPlaceHolder: '-',
            isVisibleFixedCustomBubble: true,
            bubbleStyle: { width: 'auto' },
            renderCustomBubbleContent: (cvm: any) => (
              <>
                <strong>{cvm?.game_title || '-'}</strong>
                <p style={{ opacity: 0.86 }}>Studio: {cvm?.studio || '-'}</p>
                <p style={{ opacity: 0.86 }}>Genre: {cvm?.genre || '-'}</p>
                {/* <p style={{ opacity: 0.86 }}>
                  Biz Model: {cvm?.os_type || '-'}
                </p> */}
              </>
            ),
          },
        },
      },
      {
        keyName: 'date',
        title: `Date`,
        isTips: true,
        width: 120,
        align: 'left',
        tdOpt: {
          customize: {
            render: (text: string) => {
              if (text.date) {
                return moment(text.date).format('YYYY-MM');
              }
              return (
                <>
                  {moment(text.start_date).format('YYYY-MM')}
                  <br /><span style={{ color: '#9b9fb0' }}>~</span><br />
                  {moment(text.end_date).format('YYYY-MM')}
                </>
              );
            },
          },
        },
      },
    ];
    Object.entries(METRIC_NAME_LIST)?.forEach(([key, values]) => {
      if (!collapsedFields?.includes(key)) {
        const {
          definition = '-',
          source = '-',
          update_frequency = '-',
        } = metricTipsData?.find((task) => task.metric === key) || {};
        res.push({
          ...defaultColumns,
          keyName: key,
          title: values.textEn,
          ...values,
          renderTips: () => {
            const data = {
              definition,
              source,
              updateCadence: update_frequency,
            };
            return getMetricInfoListUICommon(data);
          },
          tdOpt: getActualTdOpt(
            `${key}_value`,
            `${key}_yoy_percent`,
            `${key}_mom_percent`,
            yoy,
            mom,
          ),
        });
      }
    });
    return res;
  }, [collapsedFields, defaultColumns, mom, yoy]);
  const res = useMemo(
    () =>
      columnsOptions?.map((t: any, i: number) => {
        const isLastCol = i === columnsOptions?.length - 1;
        const {
          keyName = '',
          title, // <time>表示这个槽位取后端对应字段time数据，\n表示换行展示
          isTips = false,
          renderTips = null, // 渲染提示内容
          isHasDoubleTitle = false, // 是否有两行title的情况
          width, // 列宽
          align = 'left',
          fixed,
          tdOpt, // td渲染相关的配置
        } = t || {};
        const headers: [string, string] = title.split('\n') as [string, string];
        const headerFirTxt = isHasDoubleTitle ? headers?.[0] : title;
        const headerSecTxt = isHasDoubleTitle ? headers?.[1] : '';

        return {
          key: keyName,
          header: (
            <div
              // className="dbg-table-header-text-container"
              className={cls('dbg-table-header-text-container')}
            >
              <div className="dbg-table-header-text ">
                <InfoBubble
                  className="globalpedia-modal-font14 gp-general-table-bubble-line-height dbg-bubble-max-width"
                  placement={isLastCol ? 'top-end' : undefined}
                  customBubbleContent={
                    isTips ? renderTips?.(keyName) : undefined
                  }
                  DomOrText={
                    <>
                      {headerFirTxt}
                      {isHasDoubleTitle && (
                        <>
                          <br />
                          {headerSecTxt}
                        </>
                      )}
                    </>
                  }
                />
              </div>
              {formFields?.includes(keyName) && (
                <CommonBubble
                  content={
                    isFieldCollapsed?.includes(keyName) ? 'Unfold' : 'Fold'
                  }
                >
                  <div
                    className={cls(
                      'dbg-table-header-text-container-arrow-icon',
                      {
                        'dbg-table-header-text-container-arrow-icon-collapsed':
                          isFieldCollapsed?.includes(keyName),
                      },
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFieldDependencies(t.keyName);
                    }}
                  >
                    <LeftTriangle />
                  </div>
                </CommonBubble>
              )}
            </div>
          ),
          align,
          fixed,
          width,
          render: (cvm: any) => {
            const typeKey =
              Object.prototype.toString.call(tdOpt) === '[object Object]'
                ? Object?.keys(tdOpt as object)?.[0] || ''
                : '';
            let con: JSX.Element | string = '-';
            const {
              domain,
              src = '', // 取值的字段名
              title = '', // 取值的字段名
              platform = '', // 取值的字段名
              platformPlaceHolder,
              platformTranformSolt = false, // 把platform转换成solt显示
              renderSolt = null, // 渲染solt插槽
              isVisibleFixedCustomBubble = false, // 是否展示固定格式气泡内容
              bubbleClassName,
              bubbleStyle,
              render = null,
              renderCustomBubbleContent = null, // 渲染固定格式气泡内容
            } = tdOpt?.[typeKey] || {};
            switch (typeKey) {
              case 'text':
                con = tdOpt?.[typeKey]?.text || '-';
                break;

              case 'number':
                con = !collapsedFields?.includes(keyName) ? (
                  <CommonValueTd
                    tdAlign={align}
                    options={tdOpt?.[typeKey]?.options}
                    data={cvm}
                  />
                ) : (
                  ''
                );
                break;

              case 'project':
                con = (
                  <ProjectItem
                    domain={domain}
                    src={cvm?.[src || '']}
                    title={cvm?.[title || '']}
                    platform={detectPlatforms(cvm?.os_type)}
                    platformPlaceHolder={platformPlaceHolder}
                    platformTranformSolt={platformTranformSolt}
                    solt={renderSolt?.(cvm)}
                    data={cvm}
                    isVisibleFixedCustomBubble={isVisibleFixedCustomBubble}
                    bubbleClassName={bubbleClassName}
                    bubbleStyle={bubbleStyle}
                    customBubbleContent={renderCustomBubbleContent?.(cvm)}
                  />
                );
                break;

              case 'customize':
                con = render?.(cvm) || '-';
                break;

              default:
                con = cvm?.[tdOpt?.text?.text || ''] || '-';
                break;
            }

            return con;
          },
        };
      }),
    [
      collapsedFields,
      columnsOptions,
      formFields,
      isFieldCollapsed,
      toggleFieldDependencies,
    ],
  );
  return res;
};


// 获取表格合并列配置
export const getRowGroupNumListArr = (data) => {
  if (isEmpty(data)) return [];

  // Calculate actual row count for each project based on flattened data
  return data.map((item) => {
    const { metrics = [] } = item;

    // Get all unique dates from all metrics for this project
    const timelines = metrics.flatMap((m) => m.timeline || []);
    const uniqueDates = [...new Set(timelines.map((t) => t.date))];

    // Return the actual number of rows this project will have
    return uniqueDates.length;
  });
};

export const flattenTimelineData = (data?: any[]) => {
  if (isEmpty(data)) return [];

  return data?.flatMap((item) => {
    const { metrics = [], ...rest } = item;

    // 获取所有时间点（假设所有metric的时间点相同）
    const timelines = metrics.flatMap((m) => m.timeline || []);
    const uniqueDates = [...new Set(timelines.map((t) => t.date))];

    return uniqueDates.map((date) => {
      const record = { ...rest, date };

      metrics.forEach((metric) => {
        const { metric: metricName, timeline = [] } = metric;
        const timelineItem = timeline.find((t) => t.date === date);

        if (timelineItem) {
          // 添加当前metric的数据
          record[`${metricName}_value`] = timelineItem.value;
          record[`${metricName}_yoy_percent`] =
            typeof timelineItem.yoy_percent === 'number'
              ? timelineItem.yoy_percent / 100
              : timelineItem.yoy_percent;
          record[`${metricName}_mom_percent`] =
            typeof timelineItem.mom_percent === 'number'
              ? timelineItem.mom_percent / 100
              : timelineItem.mom_percent;
          record.id = `${rest?.game_title}_${date}`;
          record.mergeDate = `${moment(timelineItem.date).format('YYYY-MM')}`;
        }
      });

      return record;
    });
  });
};

/**
 * 扁平化 dimension_breakdown 时间线数据（isAggregate 为 false）
 * 参考 flattenTimelineData：Project/Studio 取父级，Date 取 breakdown.date，
 * Platform/Region/Country 取 breakdown，指标取 breakdown.metrics
 * @param data 待处理的数据数组（data.list）
 * @returns 处理后的扁平化数据
 */
export const flattenTimelineDataBreakdown = (data?: any[]) => {
  if (isEmpty(data)) return [];

  return data?.flatMap((item: any) => {
    const { dimension_breakdown: dimensionBreakdown = [], ...rest } = item;

    return (dimensionBreakdown || []).map((breakdown: any) => {
      const { metrics: breakdownMetrics, date, platform, region_name_en, country_name_en } = breakdown || {};
      const record: any = {
        ...rest,
        date,
        platform: platform || '-',
        region: region_name_en || '-',
        country: country_name_en || '-',
        id: `${rest?.game_title}_${platform}_${region_name_en}_${country_name_en}_${date}`,
        mergeDate: date ? moment(date).format('YYYY-MM') : '-',
      };

      (breakdownMetrics || []).forEach((metric: any) => {
        const { metric: metricName, value, yoy_percent: yoyPercent, mom_percent: momPercent } = metric || {};
        record[`${metricName}_value`] = value;
        record[`${metricName}_yoy_percent`] = typeof yoyPercent === 'number' ? yoyPercent / 100 : yoyPercent;
        record[`${metricName}_mom_percent`] = typeof momPercent === 'number' ? momPercent / 100 : momPercent;
      });

      return record;
    });
  });
};

/**
 * 生成带有_value、_mom_percent和_yoy_percent后缀的列配置
 * @param {Array} originalColumns 原始列配置数组
 * @param {Array} excludeKeys 需要排除的键名数组
 * @returns {Array} 处理后的列配置数组
 */
export function generateExtendedColumns({
  columns: originalColumns,
  excludeKeys = ['project', 'studio', 'date', 'mergeDate', 'game_title'],
  selectYM,
  downloadOptions,
}: any) {
  if (isEmpty(originalColumns)) return [];
  const extendedColumns = [];
  const mapKey = {
    project: {
      key: 'game_title',
      header: 'Project',
    },
    studio: {
      key: 'studio',
      header: 'Studio',
    },
    date: {
      key: 'mergeDate',
      header: 'Date',
    },
    game_title: {
      key: 'game_title',
      header: 'Project',
    },
  };

  const dataSource = cloneDeep(originalColumns);
  dataSource.forEach((column) => {
    column.header = METRIC_NAME_LIST?.[column.key]?.textCn;

    if (excludeKeys.includes(column.key)) {
      // 如果是排除的键，直接添加
      extendedColumns.push(column);
      column.header = mapKey[column?.key]?.header || '-';
      column.key = mapKey[column.key].key;

      // 在 date 列之后插入维度列（Platform/Region/Country）
      if (column.key === 'mergeDate' && downloadOptions) {
        if (downloadOptions.includePlatform) {
          extendedColumns.push({ key: 'platform', header: 'Platform' });
        }
        if (downloadOptions.includeRegion) {
          extendedColumns.push({ key: 'region', header: 'Region' });
        }
        if (downloadOptions.includeCountry) {
          extendedColumns.push({ key: 'country', header: 'Country' });
        }
      }
      return;
    }
    // 为每个键添加三种列配置
    extendedColumns.push({
      ...column,
      key: `${column.key}_value`,
      header: column.header || column.key,
    });

    selectYM.includes('2') && extendedColumns.push({
      key: `${column.key}_mom_percent`,
      header: `${column.header || column.key} MoM(%)`,
      align: 'right',
      width: column.width || 220,
    });

    selectYM.includes('1') && extendedColumns.push({
      key: `${column.key}_yoy_percent`,
      header: `${column.header || column.key} YoY(%)`,
      align: 'right',
      width: column.width || 220,
    });
  });

  return extendedColumns;
}

// 转换函数
export function downloadColumnsFormData(data) {
  // 先创建原始数据数组
  const result = Object.keys(data).map((key) => ({
    key,
  }));
  // 在数组开头插入3条数据
  result.unshift(
    { key: 'project' }, // 第一条插入的数据
    { key: 'studio' },
    { key: 'date' }, // 第二条插入的数据
  );

  return result;
}
